// PWA utilities for notification management and service worker interactions

/**
 * Register service worker notification click handler
 */
export const registerNotificationClickHandler = () => {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "NOTIFICATION_CLICK") {
        const { conversationId, customerName } = event.data;
        console.log(
          `🔔 Notification clicked for conversation: ${conversationId}`
        );

        // Navigate to the conversation
        if (conversationId) {
          const url = `/admin/chats/${conversationId}`;
          // If app is already open, navigate directly
          if (window.location.pathname !== url) {
            window.location.href = url;
          }
          // Focus the window
          window.focus();
        }
      }
    });
  }
};

/**
 * Check if PWA is installed
 */
export const isPWAInstalled = () => {
  // Check if running in standalone mode (installed PWA)
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone ||
    document.referrer.includes("android-app://")
  );
};

/**
 * Check if device supports PWA installation
 */
export const canInstallPWA = () => {
  return "serviceWorker" in navigator && "PushManager" in window;
};

/**
 * Get install prompt if available
 */
export const getInstallPrompt = () => {
  return window.deferredPrompt || null;
};

/**
 * Set install prompt (used by beforeinstallprompt event)
 */
export const setInstallPrompt = (prompt) => {
  window.deferredPrompt = prompt;
};

/**
 * Clear install prompt after installation
 */
export const clearInstallPrompt = () => {
  window.deferredPrompt = null;
};

/**
 * Request PWA installation
 */
export const requestPWAInstall = async () => {
  const prompt = getInstallPrompt();

  if (!prompt) {
    console.log("📱 Install prompt not available");
    return false;
  }

  try {
    // Show the install prompt
    const result = await prompt.prompt();
    console.log("📱 Install prompt result:", result);

    // Wait for the user to respond to the prompt
    const { outcome } = await prompt.userChoice;
    console.log("📱 User choice:", outcome);

    // Clear the prompt regardless of outcome
    clearInstallPrompt();

    return outcome === "accepted";
  } catch (error) {
    console.error("📱 Error during install:", error);
    return false;
  }
};

/**
 * Show install instructions for iOS Safari
 */
export const showiOSInstallInstructions = () => {
  return {
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isSafari:
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
    steps: [
      "Tap the Share button at the bottom of the screen",
      'Scroll down and tap "Add to Home Screen"',
      'Tap "Add" to install the app',
    ],
  };
};

/**
 * Detect if running on mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Get PWA display mode
 */
export const getPWADisplayMode = () => {
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  return "browser";
};

/**
 * Log PWA installation analytics
 */
export const logPWAInstall = () => {
  console.log("📱 PWA installed successfully");

  // You can add analytics tracking here
  // Example: gtag('event', 'pwa_install', { method: 'prompt' });

  // Store installation timestamp
  localStorage.setItem("pwa_installed_at", new Date().toISOString());
  localStorage.setItem("pwa_display_mode", getPWADisplayMode());
};

/**
 * Check if notifications are supported and permitted
 */
export const getNotificationStatus = () => {
  if (!("Notification" in window)) {
    return { supported: false, permission: "not-supported" };
  }

  return {
    supported: true,
    permission: Notification.permission,
    canRequest: Notification.permission === "default",
  };
};

/**
 * Request notification permission with user-friendly messaging
 */
export const requestNotificationPermission = async () => {
  const status = getNotificationStatus();

  if (!status.supported) {
    console.log("🔔 Notifications not supported");
    return false;
  }

  if (status.permission === "granted") {
    console.log("🔔 Notification permission already granted");
    return true;
  }

  if (status.permission === "denied") {
    console.log("🔔 Notification permission denied");
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    console.log("🔔 Notification permission result:", permission);
    return permission === "granted";
  } catch (error) {
    console.error("🔔 Error requesting notification permission:", error);
    return false;
  }
};
